/* The Rate Reset: the words. Notices, reminders and replies, merged per client.
   Needs engine.js first. Classic script (window.RateResetTexts) and Node module (tests).
   Every notice carries what the rules ask for (research/04 §5.1 #4): old and new price, the date of the
   first visit at the new price, the schedule, how to reach the owner and how to change or stop service. */
(function (root) {
  'use strict';
  const RR = typeof module === 'object' && module.exports ? require('./engine.js') : root.RateReset;

  const REASONS = {
    none: { label: 'No reason given', text: '' },
    costs: { label: 'Costs went up', text: 'Supplies, gas and insurance all cost more than they did, and this change keeps your service at the standard you’re used to.' },
    wages: { label: 'Pay for cleaners went up', team: 'The cost of paying and keeping good cleaners has gone up, and this change lets us keep the same team and the same care in your home.', solo: 'My costs have gone up, and this change lets me keep giving your home the same care.' },
    minwage: { label: 'Minimum wage is rising', text: 'With the minimum wage going up, pay for our cleaners is rising too, and this change lets us keep the same team and the same care in your home.' },
    time: { label: 'The home takes longer now', text: 'Your home takes longer to clean than when we started, and the new rate reflects the time it takes now.' },
    years: { label: 'First change in years', text: 'This is the first change to your rate since {since}.' }
  };

  const REPLIES = [
    { id: 'keep', when: '“Can you keep my old price?”',
      text: 'I understand, and I appreciate you asking. Your rate has stayed at {old} since {since}, and keeping it there isn’t possible anymore if I’m going to keep giving your home the care it gets now. The new rate of {new} is what it takes. If that’s more than you’d like to spend, we could talk about a shorter visit that focuses on the rooms that matter most to you.' },
    { id: 'less', when: '“We’ll have to come less often.”',
      text: 'Of course, we can change your schedule. Just so you know, visits further apart take longer, so cleaning {altFreq} would be {altPrice} per visit. Would you like me to switch you starting {date}?' },
    { id: 'fixed', when: '“We’re on a fixed income.”',
      text: 'Thank you for telling me, and I understand. I’d like to keep cleaning for you. Your new rate will be {new} from {date}. If that doesn’t fit your budget, a shorter visit could keep the cost closer to what you pay now. Just let me know which you’d prefer.' },
    { id: 'jump', when: '“That’s a big jump.”',
      text: 'You’re right that it’s a bigger change than usual. Your rate hasn’t changed since {since}, while what it takes to clean your home has. {stepsLine}' },
    { id: 'quiet', when: 'No reply after a few days',
      text: 'Hi {first}, just checking that you saw my note about your new rate of {new} from {date}. No need to reply unless you have questions. Thank you!' },
    { id: 'leave', when: '“We’re going to find someone else.”',
      text: 'I understand, and thank you for letting me know. It’s been a pleasure cleaning your home{sinceClause}. I’m happy to keep your visits at your current rate until {date}. If you ever want to come back, just reach out.' },
    { id: 'yes', when: '“Sounds good.”',
      text: 'Thank you, {first}! I really appreciate it. See you at your next visit.' }
  ];

  function firstName(name) {
    const n = String(name || '').trim();
    if (!n) return 'there';
    const parts = n.split(/\s+/);
    // "The Millers", "Mrs. Lee", "Dr. Patel", "Garcia Family": keep as written
    if (parts.length > 1 && (/^(the|mr\.?|mrs\.?|ms\.?|miss|dr\.?)$/i.test(parts[0]) || /^(family|household)$/i.test(parts[parts.length - 1]))) return n;
    return parts[0];
  }

  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : vars[k]));
  }

  // "$140 from January 1, 2027, and $165 from July 1, 2027" (every step, so a client is never told two when there are three)
  function stepList(steps) {
    const each = steps.map(s => RR.money(s.price) + ' from ' + RR.longDate(s.from));
    return each.length > 1 ? each.slice(0, -1).join(', ') + ', and ' + each[each.length - 1] : each.join('');
  }

  // Everything a notice can say about one client, from the plan.
  function vars(c, p, biz, extra) {
    const RRm = RR.money;
    const steps = p.steps || [];
    const s1 = steps[0], s2 = steps[1];
    const freq = (RR.FREQUENCIES[c.freq] || RR.FREQUENCIES.w2).short;
    const since = c.lastRaise || c.since || '';
    const team = biz.payModel !== 'owner';
    const date = s1 ? s1.from : RR.effectiveDate(biz);
    return Object.assign({
      first: firstName(c.name), name: c.name || '',
      biz: biz.name || 'our cleaning service', owner: biz.ownerName || '', phone: biz.phone || '', email: biz.email || '',
      old: RRm(+c.price), new: s1 ? RRm(s1.price) : RRm(+c.price), freq,
      date: RR.longDate(date), dateShort: RR.shortDate(date),
      since: since || 'we started', sinceClause: c.since ? ' since ' + c.since : '',
      step2: s2 ? RRm(s2.price) : '', step2Date: s2 ? RR.longDate(s2.from) : '',
      stepsLine: s2
        ? 'To keep it fair, I’m doing it in ' + (['', 'one', 'two', 'three', 'four', 'five'][steps.length] || steps.length) + ' steps: ' + stepList(steps) + '.'
        : 'I’ve kept the change as small as I can, and your rate will be ' + (s1 ? RRm(s1.price) : '') + ' from ' + RR.longDate(date) + '.',
      we: team ? 'we' : 'I', us: team ? 'us' : 'me', our: team ? 'our' : 'my',
      altFreq: '[schedule]', altPrice: '[price]', today: RR.longDate(RR.todayIso())
    }, extra || {});
  }

  function reasonText(reasons, biz, v) {
    const team = biz.payModel !== 'owner';
    return (reasons || []).map(id => {
      const r = REASONS[id];
      if (!r) return '';
      return fill(r.text != null ? r.text : (team ? r.team : r.solo), v);
    }).filter(Boolean).join(' ');
  }

  function contactLine(v, form) {
    const ways = [];
    if (v.phone) ways.push('call or text ' + (v.owner ? 'me' : 'us') + ' at ' + v.phone);
    if (v.email && form !== 'email') ways.push('email ' + v.email);
    if (form === 'email') ways.unshift('reply to this email');
    const how = ways.length ? ways.length > 1 ? ways.slice(0, -1).join(', ') + ' or ' + ways[ways.length - 1] : ways[0] : 'let ' + (v.owner ? 'me' : 'us') + ' know';
    return 'If you have questions, or you’d like to change or stop your service, just ' + how + '.';
  }

  // The notice in four forms. opts: { tone: 'warm' | 'brief', reasons: [ids], step: 0 | 1 }
  function notice(c, p, biz, opts) {
    opts = opts || {};
    const step = (p.steps || [])[opts.step || 0];
    const v = vars(c, p, biz, step ? { new: RR.money(step.price), date: RR.longDate(step.from), dateShort: RR.shortDate(step.from) } : null);
    if (opts.step) v.old = RR.money((p.steps[opts.step - 1] || {}).price || +c.price);
    const why = reasonText(opts.reasons, biz, v);
    const sign = [v.owner, v.biz].filter(Boolean).join('\n');
    const thanks = v.sinceClause ? 'Thank you for letting ' + v.biz + ' take care of your home' + v.sinceClause + '.' : 'Thank you for letting ' + v.biz + ' take care of your home.';
    const change = 'Starting with your first cleaning on or after ' + v.date + ', your rate for cleaning ' + v.freq + ' will be ' + v.new + ' per visit, up from ' + v.old + '.';
    const bodyFor = form => opts.tone === 'brief' ? [
        'Hi ' + v.first + ',',
        'A quick note: starting ' + v.date + ', your rate for cleaning ' + v.freq + ' will be ' + v.new + ' per visit (it’s ' + v.old + ' today).' + (why ? ' ' + why : ''),
        contactLine(v, form),
        'Thank you for having ' + v.us + '.',
        sign
      ] : [
        String(v.name).trim() ? 'Dear ' + v.first + ',' : 'Hello,',   // never "Dear there,"
        thanks + ' It’s a pleasure to clean for you.',
        change + (why ? ' ' + why : ''),
        'Nothing else about your service changes.',
        contactLine(v, form),
        'Thank you for your trust.',
        'Warmly,\n' + sign
      ];
    const letter = [v.today].concat(bodyFor('letter')).filter(Boolean).join('\n\n');
    const email = bodyFor('email').filter(Boolean).join('\n\n');
    const subject = 'Your cleaning rate from ' + v.dateShort;
    const reach = v.phone ? ' Questions, or want to change or stop service? Just reply or call ' + v.phone + '.' : ' Questions, or want to change or stop service? Just reply.';
    const text = 'Hi ' + v.first + ', it’s ' + (v.owner || v.biz) + (v.owner && biz.name ? ' from ' + biz.name : '') +
      '. Starting ' + v.dateShort + ', your rate for cleaning ' + v.freq + ' will be ' + v.new + ' a visit (now ' + v.old + '). Thank you for having ' + v.us + '!' + reach;
    const invoice = 'Rate update: from ' + v.date + ', cleaning ' + v.freq + ' is ' + v.new + ' per visit.';
    const reminder = 'Hi ' + v.first + ', a quick reminder that your new rate of ' + v.new + ' starts with your cleaning on or after ' + v.date + '. Thank you!' + (v.owner ? ' – ' + v.owner : '');
    return { subject, letter, email, text, invoice, reminder };
  }

  function replies(c, p, biz, extra) {
    const v = vars(c, p, biz, extra);
    return REPLIES.map(r => ({ id: r.id, when: r.when, text: fill(r.text, v) }));
  }


  // A quote for a new client (the New-Client Price Builder). q: { name, prices: [{freq, price}], chosen, first, scope }
  function quote(q, biz) {
    const v = { first: firstName(q.name), owner: biz.ownerName || '', biz: biz.name || 'our cleaning service', phone: biz.phone || '' };
    const main = (q.prices || []).find(x => x.freq === q.chosen) || (q.prices || [])[0];
    if (!main) return { email: '', text: '' };
    const label = f => (RR.FREQUENCIES[f] || RR.FREQUENCIES.w2).short;
    const others = (q.prices || []).filter(x => x !== main).map(x => 'cleaning ' + label(x.freq) + ' would be ' + RR.money(x.price) + ' per visit');
    const lines = [
      'Hi ' + v.first + ',',
      'Thank you for having ' + (biz.payModel === 'owner' ? 'me' : 'us') + ' look at your home. For cleaning ' + label(main.freq) + ', the price is ' + RR.money(main.price) + ' per visit.' +
        (q.first ? ' The first visit is a deeper clean, at ' + RR.money(q.first) + '.' : ''),
      others.length ? 'If another schedule suits you better, ' + others.join(', and ') + '.' : '',
      q.scope ? 'Each visit includes ' + q.scope + '.' : '',
      'Our rates are reviewed once a year, in January, and you’ll always hear from ' + (biz.payModel === 'owner' ? 'me' : 'us') + ' in writing at least 10 days before any change.',
      'If this works for you, just reply and we’ll set a start date.',
      [v.owner, v.biz].filter(Boolean).join('\n')
    ].filter(Boolean);
    const text = 'Hi ' + v.first + ', it’s ' + (v.owner || v.biz) + '. For cleaning ' + label(main.freq) + ' the price is ' + RR.money(main.price) + ' a visit' +
      (q.first ? ', first deep clean ' + RR.money(q.first) : '') + '. If that works, reply and we’ll pick a start date!';
    return { subject: 'Your cleaning quote', email: lines.join('\n\n'), text };
  }

  const api = { REASONS, REPLIES, firstName, fill, vars, notice, replies, quote };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.RateResetTexts = api;
})(typeof window !== 'undefined' ? window : globalThis);
